import { useState, useEffect } from 'react';
import { useSpecifications } from '@/hooks/useSpecifications';
import { JobCategory, SpecificationType, SpecificationTemplate } from '@/lib/types/job';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { X, Plus, Edit, Trash2, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { MasterSpecificationBuilder } from './MasterSpecificationBuilder';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SpecificationDocumentCard } from './SpecificationDocumentCard';

interface SpecificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  specification?: SpecificationTemplate | null;
  mode: 'view' | 'edit' | 'create';
  onModeChange: (mode: 'view' | 'edit' | 'create') => void;
  isAdminView?: boolean;
}

export const SpecificationDialog = ({ 
  open, 
  onOpenChange, 
  specification, 
  mode,
  onModeChange,
  isAdminView = false
}: SpecificationDialogProps) => {
  const { toast } = useToast();
  const { createSpecification, updateSpecification, deactivateSpecification } = useSpecifications();
  
  // Determine if this is a master template or upload-based
  const isMasterTemplate = specification?.content && 'product_category_id' in specification.content;
  const isUploadBased = specification && (specification as any).specification_document_id;
  const [templateMode, setTemplateMode] = useState<'basic' | 'master'>(
    mode === 'create' ? 'master' : (isMasterTemplate ? 'master' : 'basic')
  );
  
  const [formData, setFormData] = useState({
    specification_name: '',
    category: '' as JobCategory,
    specification_type: '' as SpecificationType,
    specification_description: '',
    content: {},
    sections: [] as any[],
    tags: [] as string[],
    is_standard: false,
  });

  const [newTag, setNewTag] = useState('');
  const [newSection, setNewSection] = useState({ title: '', content: '' });

  const jobCategories: JobCategory[] = ['print_materials', 'packaging', 'signage', 'merchandising', 'other'];
  const specificationTypes: SpecificationType[] = ['my_organization', 'personal'];

  const getTypeLabel = (type: SpecificationType) => {
    const labels: Record<SpecificationType, string> = {
      my_organization: 'My Organization',
      personal: 'Personal'
    };
    return labels[type];
  };

  useEffect(() => {
    if (specification && (mode === 'view' || mode === 'edit')) {
      setFormData({
        specification_name: specification.specification_name,
        category: specification.category,
        specification_type: specification.specification_type,
        specification_description: specification.specification_description || '',
        content: specification.content || {},
        sections: specification.sections || [],
        tags: specification.tags || [],
        is_standard: (specification as any).is_standard || false,
      });
      // Set template mode based on whether it's a master template
      setTemplateMode(isMasterTemplate ? 'master' : 'basic');
    } else if (mode === 'create') {
      setFormData({
        specification_name: '',
        category: '' as JobCategory,
        specification_type: '' as SpecificationType,
        specification_description: '',
        content: {},
        sections: [],
        tags: [],
        is_standard: false,
      });
      // Default to master template for new specs
      setTemplateMode('master');
    }
  }, [specification, mode, isMasterTemplate]);

  const handleSave = async () => {
    if (!formData.specification_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Specification name is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (mode === 'create') {
        await createSpecification(formData);
      } else if (mode === 'edit' && specification) {
        await updateSpecification(specification.id, formData);
      }
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleMasterTemplateSave = async (specData: any) => {
    try {
      await createSpecification(specData);
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleDelete = async () => {
    if (!specification) return;
    
    try {
      await deactivateSpecification(specification.id);
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });
  };

  const addSection = () => {
    if (newSection.title.trim()) {
      setFormData({
        ...formData,
        sections: [...formData.sections, { ...newSection }],
      });
      setNewSection({ title: '', content: '' });
    }
  };

  const removeSection = (index: number) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter((_, i) => i !== index),
    });
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isViewMode && 'View Specification'}
            {isEditMode && 'Edit Specification'}
            {isCreateMode && 'Create New Specification'}
          </DialogTitle>
          <DialogDescription>
            {isViewMode && 'Specification details'}
            {isEditMode && 'Update specification information'}
            {isCreateMode && 'Create a reusable specification template'}
          </DialogDescription>
        </DialogHeader>


        {/* Show master template builder for master mode OR upload-based specs */}
        {(templateMode === 'master' || isUploadBased) ? (
          <MasterSpecificationBuilder
            existingSpecification={specification}
            isEditMode={isEditMode}
            onSave={isEditMode ? async (specData) => {
              await updateSpecification(specification!.id, specData);
              onOpenChange(false);
            } : handleMasterTemplateSave}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="spec-name">
              Specification Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="spec-name"
              value={formData.specification_name}
              onChange={(e) => setFormData({ ...formData, specification_name: e.target.value })}
              placeholder="e.g., ISO 9001 Quality Requirements"
              disabled={isViewMode}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v as JobCategory })}
                disabled={isViewMode}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {jobCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="spec-type">Specification Type</Label>
              <Select
                value={formData.specification_type}
                onValueChange={(v) => setFormData({ ...formData, specification_type: v as SpecificationType })}
                disabled={isViewMode}
              >
                <SelectTrigger id="spec-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {specificationTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {getTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.specification_description}
              onChange={(e) => setFormData({ ...formData, specification_description: e.target.value })}
              placeholder="Brief description of this specification template"
              rows={3}
              disabled={isViewMode}
            />
          </div>

          {isAdminView && !isViewMode && (
            <div className="flex items-center space-x-2 p-4 border border-border rounded-md bg-secondary/20">
              <input
                type="checkbox"
                id="is-standard"
                checked={formData.is_standard}
                onChange={(e) => setFormData({ ...formData, is_standard: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is-standard" className="cursor-pointer font-medium">
                Mark as Standard Template
                <span className="block text-xs text-muted-foreground font-normal mt-1">
                  Standard templates are visible to all organizations
                </span>
              </Label>
            </div>
          )}

          <div className="space-y-2">
            <Label>Tags</Label>
            {!isViewMode && (
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="secondary" onClick={addTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  {!isViewMode && (
                    <button onClick={() => removeTag(tag)} className="ml-1 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sections</Label>
            {!isViewMode && (
              <Card className="p-4 space-y-3">
                <Input
                  value={newSection.title}
                  onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                  placeholder="Section title"
                />
                <Textarea
                  value={newSection.content}
                  onChange={(e) => setNewSection({ ...newSection, content: e.target.value })}
                  placeholder="Section content"
                  rows={3}
                />
                <Button type="button" variant="secondary" onClick={addSection} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Section
                </Button>
              </Card>
            )}
            <div className="space-y-3 mt-3">
              {formData.sections.map((section, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{section.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{section.content}</p>
                    </div>
                    {!isViewMode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSection(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
          </div>
        )}

        {/* Only show footer when not creating/editing master templates (they have their own buttons) */}
        {!((templateMode === 'master' || isUploadBased) && (isCreateMode || isEditMode)) && (
        <DialogFooter className="flex items-center justify-between">
          <div>
            {(isEditMode || isViewMode) && specification && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
              >
                Delete Specification
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {isViewMode && (
              <Button type="button" onClick={() => onModeChange('edit')}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            {!isViewMode && (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave}>
                  Save Specification
                </Button>
              </>
            )}
            {isViewMode && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
